import {
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  LOCK_WARNING,
  ListContext,
  ListContextValue,
  LockedIcon,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, NonPersonnel, Permission, PermissionAction } from "generated/openApi";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const NonPersonnelPageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  reroute: (id: number, crudMode: CrudMode) => void;
  nonPersonnel: NonPersonnel;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.nonPersonnel.id;

  const save = (nonPersonnel: NonPersonnel) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.nonPersonnels
        .saveUpdate(nonPersonnel)
        .then(() => {
          props.reroute(id, "view");
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    } else {
      ApiService.nonPersonnels
        .saveCreate(nonPersonnel)
        .then((response) => {
          const id = response;
          props.reroute(id, "view");
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.nonPersonnels.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const toggleLock = (id: number) => {
    if (window.confirm(LOCK_WARNING)) {
      ApiService.nonPersonnels.toggleLock(id).then((response) => {
        navigate("../");
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (isAuthorized(props.rootPermission, PermissionAction.Lock)) {
      if (!props.nonPersonnel.isLocked)
        menuItems.push({
          label: <Typography color="error">Lock</Typography>,
          action: () => toggleLock(id),
        });
      else
        menuItems.push({
          label: "Unlock",
          action: () => toggleLock(id),
        });
    }

    if (
      isAuthorized(props.rootPermission, PermissionAction.Delete) &&
      (!props.nonPersonnel.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock))
    )
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit">
          {id === 0 ? "New" : ""} {props.handle}
        </Typography>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {props.nonPersonnel.isLocked === true && <LockedIcon />}

        {id > 0 && <DataModelInformation dataModelType={DataModelEnum.NonPersonnel} dataModelId={id} />}

        <CloseButton confirm={props.unsavedChanges} />

        {props.crudMode === "view" &&
          isAuthorized(props.rootPermission, PermissionAction.Update) &&
          (!props.nonPersonnel.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
            <UpdateButton onClick={() => props.reroute(id, "edit")} />
          )}

        {props.crudMode === "edit" && (
          <SubmitButton onClick={() => save(props.nonPersonnel)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
};
